import type { YearDivider } from '@/utils/chartAxis';

export type { YearDivider };

interface ChartGuidesProps {
  yearDividers?: YearDivider[];
  /** X positions (percent) that receive a small month tick dot. */
  ticks?: number[];
  /** X position (percent) of the hovered point, or null when idle. */
  crosshair?: number | null;
  /** Drop tick dots just below the plot so they never sit on top of the bars. */
  ticksBelow?: boolean;
  isDark?: boolean;
  /** Crosshair accent: primary for the equity line, secondary for drawdown. */
  accent?: 'primary' | 'secondary';
}

/**
 * Shared overlay for the performance charts: a faint dashed divider wherever a
 * new calendar year begins (with a tiny year badge so the boundary is obvious
 * without costing any x-axis space), a small tick dot under each labelled
 * month, and a vertical crosshair that snaps to the hovered point. Purely
 * decorative — never intercepts pointer events.
 */
export default function ChartGuides({
  yearDividers = [],
  ticks = [],
  crosshair = null,
  ticksBelow = false,
  isDark = false,
  accent = 'primary',
}: ChartGuidesProps) {
  const yearLineColor = isDark ? 'border-background-50/20' : 'border-foreground-300/70';
  const badgeClass = isDark
    ? 'bg-background-50/20 text-background-100'
    : 'bg-background-200 text-foreground-600';
  const tickColor = isDark ? 'bg-background-200/40' : 'bg-foreground-300';
  const crosshairColor =
    accent === 'secondary'
      ? isDark
        ? 'bg-secondary-300/60'
        : 'bg-secondary-500/50'
      : isDark
        ? 'bg-accent-400/60'
        : 'bg-primary-500/50';

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {/* year dividers */}
      {yearDividers.map((divider) => (
        <div
          key={`year-line-${divider.x}`}
          className={`absolute top-0 bottom-0 border-l border-dashed ${yearLineColor}`}
          style={{ left: `${divider.x}%` }}
        ></div>
      ))}

      {/* year badges — pinned to the top so they never eat x-axis space */}
      {yearDividers.map((divider) => (
        <span
          key={`year-badge-${divider.x}`}
          className={`absolute top-0.5 -translate-x-1/2 rounded-full px-1.5 py-px font-num text-[9px] font-semibold leading-none ${badgeClass}`}
          style={{ left: `${divider.x}%` }}
        >
          {divider.label}
        </span>
      ))}

      {/* month tick dots */}
      {ticks.map((x, i) => (
        <span
          key={`tick-${x}-${i}`}
          className={`absolute ${ticksBelow ? '-bottom-2.5' : 'bottom-0'} h-1.5 w-1.5 -translate-x-1/2 rounded-full ${tickColor}`}
          style={{ left: `${x}%` }}
        ></span>
      ))}

      {/* hover crosshair */}
      {crosshair !== null && (
        <div
          className={`absolute top-0 bottom-0 w-px ${crosshairColor}`}
          style={{ left: `${crosshair}%` }}
        ></div>
      )}
    </div>
  );
}